import Icon from '@rio-cloud/rio-uikit/Icon';
import StatsWidget from '@rio-cloud/rio-uikit/StatsWidget';
import StatsWidgets from '@rio-cloud/rio-uikit/StatsWidgets';
import { FormattedMessage } from 'react-intl';

import type { BookingOverviewStatistics } from './bookingOverviewService';

interface Props {
    statistics: BookingOverviewStatistics;
}

const iconContainerClassName =
    'height-50 aspect-ratio-1 bg-lightest rounded display-grid place-items-center text-size-14';
const bodyClassName = 'flex-1-1 display-flex gap-15 padding-20';
const footerClassName = 'text-size-12 padding-x-20 padding-y-10 bg-lightest rounded-bottom display-flex gap-5';
const labelClassName = 'label label-condensed cursor-pointer display-flex align-items-center';

function VehicleType({ icon, count }: { icon: string; count: number }) {
    if (count === 0) {
        return null;
    }

    return (
        <div className='display-flex align-items-center user-select-none cursor-pointer gap-3 padding-x-5 rounded'>
            <span className={`uikit-icon icon-${icon} text-size-16`} />
            <span className='TreeLabelCount label label-condensed'>{count}</span>
        </div>
    );
}

function StatisticLabel({
    count,
    messageId,
    className,
}: {
    count: number;
    messageId:
        | 'intl-msg:marketplace.bookings.overview.stats.noRenewalScheduled'
        | 'intl-msg:marketplace.bookings.overview.stats.nextMonth'
        | 'intl-msg:marketplace.bookings.overview.stats.thisMonth'
        | 'intl-msg:marketplace.free.trial';
    className: string;
}) {
    if (count === 0) {
        return null;
    }

    return (
        <span className={`${labelClassName} ${className}`}>
            <span>
                {count} <FormattedMessage id={messageId} />
            </span>
        </span>
    );
}

export function BookingOverviewStats({ statistics }: Props) {
    return (
        <StatsWidgets className='display-grid grid-auto-fit-250 margin-bottom-25'>
            <StatsWidget className='shadow-default display-flex flex-column'>
                <div className={bodyClassName}>
                    <div className={iconContainerClassName}>
                        <Icon iconName='truck' size='14' iconClassName='text-size-200pct' />
                    </div>
                    <div>
                        <div className='text-color-dark'>
                            <FormattedMessage id='intl-msg:marketplace.preview.multilevel.vehicles' />
                        </div>
                        <div className='text-size-h3 text-color-darkest line-height-125rel'>
                            {statistics.vehicles.total}
                        </div>
                    </div>
                </div>
                <div className='text-size-12 padding-x-15 padding-y-10 bg-lightest rounded-bottom display-flex gap-3'>
                    <VehicleType icon='truck' count={statistics.vehicles.trucks} />
                    <VehicleType icon='bus' count={statistics.vehicles.buses} />
                    <VehicleType icon='van' count={statistics.vehicles.vans} />
                </div>
            </StatsWidget>

            <StatsWidget className='shadow-default display-flex flex-column'>
                <div className={bodyClassName}>
                    <div className={iconContainerClassName}>
                        <Icon iconName='signature' size='14' iconClassName='text-size-200pct' />
                    </div>
                    <div>
                        <div className='text-color-dark'>
                            <FormattedMessage id='intl-msg:marketplace.contracts' />
                        </div>
                        <div className='text-size-h3 text-color-darkest line-height-125rel'>
                            {statistics.contracts.total}
                        </div>
                    </div>
                </div>
                <div className={footerClassName}>
                    <StatisticLabel
                        count={statistics.contracts.freeTrial}
                        messageId='intl-msg:marketplace.free.trial'
                        className='label-secondary'
                    />
                    <StatisticLabel
                        count={statistics.contracts.noRenewalScheduled}
                        messageId='intl-msg:marketplace.bookings.overview.stats.noRenewalScheduled'
                        className='label-danger'
                    />
                </div>
            </StatsWidget>

            <StatsWidget className='shadow-default display-flex flex-column'>
                <div className={bodyClassName}>
                    <div className={iconContainerClassName}>
                        <Icon
                            iconName='signature'
                            size='14'
                            iconClassName='text-size-200pct text-color-gray margin-left--5 margin-top--5'
                            pairIconName='time'
                            pairIconClassName='text-size-18'
                        />
                    </div>
                    <div>
                        <div className='text-color-dark text-wrap-balance'>
                            <FormattedMessage id='intl-msg:marketplace.bookings.overview.stats.terminatingSoon' />
                        </div>
                        <div className='text-size-h3 text-color-darkest line-height-125rel'>
                            {statistics.terminatingSoon.total}
                        </div>
                    </div>
                </div>
                <div className={footerClassName}>
                    <StatisticLabel
                        count={statistics.terminatingSoon.nextMonth}
                        messageId='intl-msg:marketplace.bookings.overview.stats.nextMonth'
                        className='label-warning'
                    />
                    <StatisticLabel
                        count={statistics.terminatingSoon.thisMonth}
                        messageId='intl-msg:marketplace.bookings.overview.stats.thisMonth'
                        className='label-danger'
                    />
                </div>
            </StatsWidget>
        </StatsWidgets>
    );
}
